/**
 * useReservation — hook compositor del flujo de reserva.
 *
 * Su única responsabilidad es ensamblar los hooks especializados y exponer
 * la misma forma de API que el componente padre ya consume. Toda la lógica
 * de detalle vive en:
 *
 *   - hooks/useReservationForm.js     -> estado del wizard, Formik shape
 *   - hooks/useReservationPricing.js  -> montos, descuentos, split payments
 *   - hooks/useReservationApi.js      -> llamadas HTTP
 *   - services/reservationService.js  -> construcción del FormData + submit
 *   - errorHandler.js                 -> mapeo de errores backend → ES
 *
 * Nota: mantenemos la firma pública para no romper a `ReserveRoom.js` ni a
 * los step components.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BASE_CURRENCY, STAY_TYPE_NIGHT } from './constants';
import { useReservationForm } from './hooks/useReservationForm';
import { useReservationPricing } from './hooks/useReservationPricing';
import { useReservationApi } from './hooks/useReservationApi';
import { buildReservationFormData, submitReservation } from './services/reservationService';
import { extractReservationError, networkErrorMessage } from './errorHandler';

export const useReservation = () => {
  const navigate = useNavigate();
  const { roomNo } = useParams();

  // Datos del hotel del usuario (persistidos por la sesión).
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || BASE_CURRENCY;

  // Habitación seleccionada por URL params, completada cuando llegan los datos.
  const [selectedRoomData, setSelectedRoomData] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const api = useReservationApi(hotel?.hotelId);
  const form = useReservationForm(selectedRoomData);
  const pricing = useReservationPricing({
    selectedRoomData,
    roomData: api.roomData,
    stayTypes: api.stayTypes
  });

  // Asocia el roomNo de la URL con los datos completos cuando llegan.
  useEffect(() => {
    if (roomNo && api.roomData.length > 0) {
      const room = api.roomData.find((r) => r.roomNo === roomNo);
      if (room) setSelectedRoomData(room);
    }
  }, [roomNo, api.roomData]);

  // Defaultea stayType al tipo nocturno REAL una vez cargados los stay types.
  // El default legacy 'night' es un string que no coincide con ningún _id real:
  // dejaba el <Select> de "Tipo de Estancia" vacío y forzaba el camino legacy
  // que leía el precio desde room.amount (desactualizado) en vez de pricing[].
  useEffect(() => {
    if (!api.stayTypes?.length) return;
    const nightly = api.stayTypes.find((t) => t.isNightly) || api.stayTypes[0];
    if (!nightly) return;
    form.setInitialValues((prev) => {
      const isLegacyDefault = prev.stayType === STAY_TYPE_NIGHT || !prev.stayType;
      return isLegacyDefault ? { ...prev, stayType: nightly._id } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.stayTypes]);

  /**
   * Submit handler. Construye el FormData vía service, envía, y maneja la
   * respuesta. No conoce nada del DOM ni de MUI.
   */
  const AddData = async (values, resetForm) => {
    setFormSubmitting(true);

    try {
      const formData = buildReservationFormData({
        values,
        hotel: { hotelId: hotel.hotelId, _id: hotel._id },
        showSplitPayments: pricing.showSplitPayments,
        splitPayments: pricing.splitPayments,
        promoCode: pricing.promoCode,
        discountCents: pricing.discountCents
      });

      const response = await submitReservation(formData);

      if (response.status === 200) {
        toast.success('Habitación reservada exitosamente');
        navigate('/dashboard/app');
        resetForm();
        api.resetExistingCustomerData();
        pricing.resetSplitPayments();
        return;
      }

      // Errores controlados del backend (4xx).
      toast.error(extractReservationError(response));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error al crear reserva', err);
      toast.error(networkErrorMessage());
    } finally {
      setFormSubmitting(false);
    }
  };

  // Adaptadores para mantener la firma anterior de `handleUpdateSplitPayment`.
  const handleUpdateSplitPayment = (paymentId, field, value) =>
    pricing.updateSplitPayment(paymentId, field, value, api.exchangeRates);

  // ---------------------------------------------------------------------------
  // API pública (mantiene exactamente la forma que consume ReserveRoom.js).
  // ---------------------------------------------------------------------------
  return {
    // form / wizard
    activeStep: form.activeStep,
    setActiveStep: form.setActiveStep,
    initialValues: form.initialValues,
    setInitialValues: form.setInitialValues,
    handleNext: form.handleNext,
    handleBack: form.handleBack,
    getProgress: form.getProgress,

    // datos remotos
    roomData: api.roomData,
    selectedRoomData,
    setSelectedRoomData,
    exchangeRates: api.exchangeRates,
    stayTypes: api.stayTypes,
    existingCustomerData: api.existingCustomerData,
    isSearching: api.isSearching,
    setIsSearching: api.setIsSearching,
    fetchCustomerData: api.fetchCustomerData,
    hotel,
    currency,

    // pricing
    amount: pricing.amount,
    setAmount: () => {
      /* deprecated — el monto se deriva de selectedRoomData y calculateAmount */
    },
    discountAmount: pricing.discountAmount,
    finalAmount: pricing.finalAmount,
    promoCode: pricing.promoCode,
    setPromoCode: pricing.setPromoCode,
    splitPayments: pricing.splitPayments,
    setSplitPayments: pricing.setSplitPayments,
    showSplitPayments: pricing.showSplitPayments,
    setShowSplitPayments: pricing.setShowSplitPayments,
    calculateAmount: pricing.calculateAmount,
    handleTotalAmount: pricing.handleTotalAmount,
    syncTotalAmount: pricing.syncTotalAmount,
    syncForeignCurrencyAmount: pricing.syncForeignCurrencyAmount,
    handleAddSplitPayment: pricing.addSplitPayment,
    handleRemoveSplitPayment: pricing.removeSplitPayment,
    handleUpdateSplitPayment,
    getTotalPaid: pricing.getTotalPaid,
    areSplitPaymentsValid: pricing.areSplitPaymentsValid,

    // submit
    formSubmitting,
    AddData
  };
};
